import React, { useEffect, useState } from 'react'
import {
    Dropdown,
    Pagination,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Tabs,
    theme,
    Typography,
} from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { EllipsisOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

import { getAllCandidate, resetFilter, setActiveTab, setSearch } from '../store/candidate.slice'
import { PRIVATE_ROUTES } from '../../../routes'
import ProfileAvatar from './profil-avatar'
import NoteDrawer from './candidate-note-drawer'
import StatusModal from './status-modal'
import { NoteIcon, ViewIcon } from '../../../assets/icons'
import { CANDIDATE_STATUS } from './candidate-status-tab-list'
import { StatusIcon } from '../../../assets/icons/statusIcon'

const { Text } = Typography

const CandidateTable = () => {
    const { useToken } = theme
    const { token } = useToken()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { candidateList, pagination, isLoading, filter, activeTab, search } = useSelector(
        (state) => state.candidateSlice
    )

    const [noteDrawer, setNoteDrawer] = useState(false)
    const [candidateNote, setCandidateNote] = useState(null)
    const [statusModal, setStatusModal] = useState({
        open: false,
        candidate: null,
        status: null,
    })

    const [sortInfo, setSortInfo] = useState({
        sort: null,
        sortType: null,
    })


    // Reset filters once on mount
    useEffect(() => {
        dispatch(resetFilter())
        dispatch(setSearch(""))
        // dispatch(setActiveTab("applied"))
    }, [])

    // Fetch candidates whenever filters change
    useEffect(() => {
        dispatch(
            getAllCandidate({
                filter: { ...filter, status: activeTab },
                skip: (pagination.page - 1) * pagination?.limit,
                limit: pagination?.limit,
                sort: sortInfo.sort,
                sortType: sortInfo.sortType,
                search: search
            })
        )
    }, [activeTab, sortInfo, search])

    const handleTableChange = (_, __, sorter) => {
        const { columnKey, order } = sorter

        if (order === 'ascend' || order === 'descend') {
            const sortType = order === 'ascend' ? 'asc' : 'desc'
            setSortInfo({ sort: columnKey, sortType })
        } else {
            setSortInfo({ sort: null, sortType: null })
        }
    }

    const handlePaginationChange = async (page, limit) => {
        const skip = (page - 1) * limit
        await dispatch(
            getAllCandidate({
                filter: { ...filter, status: activeTab },
                skip,
                limit,
                sort: sortInfo.sort,
                sortType: sortInfo.sortType,
            })
        )
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'fullName',
            key: 'fullName',
            sorter: true,
            sortOrder:
                sortInfo.sort === 'fullName'
                    ? sortInfo.sortType === 'asc'
                        ? 'ascend'
                        : 'descend'
                    : null,
            render: (text, item) => (
                <Space>
                    <ProfileAvatar
                        profilePicture={item?.profilePicture}
                        name={text}
                    />
                    <Text>{text || '-'}</Text>
                </Space>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'personalEmail',
            key: 'personalEmail',
            sorter: true,
            sortOrder:
                sortInfo.sort === 'personalEmail'
                    ? sortInfo.sortType === 'asc'
                        ? 'ascend'
                        : 'descend'
                    : null,
            render: (text) => <Text>{text || '-'}</Text>,
        },

        {
            title: 'Mobile Number',
            dataIndex: 'primaryContactNumber',
            key: 'primaryContactNumber',
            render: (text) => {
                const formattedNumber = text
                    ? text.startsWith('91')
                        ? text.slice(2)
                        : text
                    : '-'
                return <Text>{formattedNumber}</Text>
            },
        },
        {
            title: 'Experience',
            dataIndex: 'experience',
            key: 'experience',
            render: (text) => {

                return <Text>{text ? text : "-"}</Text>
            },
        }, {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => {

                return <Text>{text ? text : "-"}</Text>
            },
        },
        {
            key: 'context',
            dataIndex: 'context',
            title: ' ',
            render: (_, row) => (
                <Row justify="end" style={{ paddingRight: 5 }}>
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        menu={{
                            items: [
                                {
                                    key: '1',
                                    label: 'View',
                                    icon: <ViewIcon />,
                                    style: {
                                        columnGap: '10px',
                                    },
                                    onClick: () => {
                                        navigate(
                                            PRIVATE_ROUTES.candidate.view(
                                                row._id
                                            )
                                        )
                                    },
                                },
                                {
                                    key: '2',
                                    label: 'Note',
                                    icon: <NoteIcon width="20px" />,
                                    style: {
                                        columnGap: '10px',
                                    },
                                    onClick: () => {
                                        setNoteDrawer(true)
                                        setCandidateNote(row)
                                    },
                                },
                                activeTab != "convertToUser" && {
                                    key: '3',
                                    label: 'Change Status',
                                    icon: <StatusIcon width="20px" />,
                                    style: {
                                        columnGap: '10px',
                                    },
                                    onClick: () => {
                                        setStatusModal({
                                            open: true,
                                            candidate: row,
                                            status: row?.status,
                                        })
                                    },
                                },
                            ],
                        }}
                    >
                        <EllipsisOutlined rotate={90} />
                    </Dropdown>
                </Row>
            ),
        },
    ]

    return (
        <div style={{ marginTop: "15px" }}>
            {/* Candidate Status Tabs */}
            <Tabs
                defaultActiveKey="applied"
                activeKey={activeTab}
                onChange={(key) => dispatch(setActiveTab(key))}
                items={CANDIDATE_STATUS.map((status) => ({
                    key: status.value,
                    label: status.label,
                }))}
            />

            <Spin spinning={isLoading}>
                <Table
                    columns={columns}
                    dataSource={candidateList}
                    scroll={{ x: 'scroll' }}
                    pagination={false}
                    rootClassName="tableGlobal"
                    style={{
                        borderRadius: '16px',
                        marginTop: '5px',
                    }}
                    showSorterTooltip={{ target: 'sorter-icon' }}
                    onChange={handleTableChange}
                />

                {candidateList?.length > 0 && (
                    <Row
                        justify="end"
                        align="middle"
                        style={{
                            border: `2px solid ${token.colorPalette.baseColor.quaternary}`,
                            padding: '15px 20px',
                            borderRadius: '0px 0px 8px 8px',
                        }}
                    >
                        <Text>
                            {pagination.page} - {pagination.totalPages} of{' '}
                            {pagination.totalCount}
                        </Text>
                        <Pagination
                            current={pagination.page}
                            pageSize={pagination.limit}
                            total={pagination.totalCount}
                            pageSizeOptions={[2, 10, 20, 50, 100]}
                            showSizeChanger
                            onChange={handlePaginationChange}
                        // disabled={pagination.totalPages <= 1}
                        />
                    </Row>
                )}
            </Spin>

            {/* Drawers & Modals */}
            <NoteDrawer
                visible={noteDrawer}
                onClose={setNoteDrawer}
                candidate={candidateNote}
            />
            <StatusModal
                activeStatus={activeTab}
                statusModal={statusModal}
                setStatusModal={setStatusModal}
                open={statusModal.open}
                selectedStatus={statusModal?.status}
                isDetails={statusModal?.candidate?.isCompleteCandidateDetails}
                onClose={() => setStatusModal({ open: false, candidate: null })}
                onChange={(newStatus) =>
                    setStatusModal((prev) => ({
                        ...prev,
                        status: newStatus,
                    }))
                }
            />
        </div>
    )
}

export default CandidateTable
